import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../provider/AuthProvider';
import { useUser } from '../../provider/UserProvider';
import { api } from '../../utils/api';
import { EmployerProfile } from '../../models/User';
import { Job } from '../../models/Job';
import { fetchJobsByEmployer, deleteJob, updateJob } from '../../services/jobService';
import { getEmployerApplications, updateApplicationStatus, Application } from '../../services/applicationService';
import SheetLoading from '../../components/shared/ui/SheetLoading';
import SheetGround from '../../components/shared/ui/SheetGround';
import { LoadingSpinner } from '../../components/shared/ui/LoadingSpinner';
import { FieldBlock, FieldRow, fieldInput, fieldArea, folioFormCss } from '../../components/shared/folio/form';
import { FolioSelect } from '../../components/shared/folio/Select';
import { ROUTE_NAMES } from '../../routes/routeConstants';
import { Download, Briefcase, PoundSterling, User, Eye, Calendar, Pencil, Trash2 } from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { downloadFile } from '../../utils/downloadFile';
import { AcaIcon } from '../../components/shared/aca';
import { AuthModal } from '../../components/ui/AuthModal';
import { VELLUM as T, SANS, MONO, mono, btn } from '../../components/shared/folio/kit';

/* The folio vocabulary under the token names these files already used. */
const pal = {
  paper: T.ground,
  card: T.ground,
  ink: T.ink,
  inkSoft: T.mute,
  inkMute: T.mute,
  rule: T.rule,
  accent: T.accent,
  onAccent: T.onAccent,
};
const fam = { sans: SANS, mono: MONO };

const label10: React.CSSProperties = { ...mono(T), fontSize: 10, letterSpacing: '0.2em' };

const ICON = 13;

// Helper functions to safely access populated fields
const getJobId = (app: Application): string => {
  if (typeof app.jobId === 'string') return app.jobId;
  return app.jobId?._id || '';
};

const getJobTitle = (app: Application): string => {
  if (typeof app.jobId === 'string') return 'Unknown Job';
  return app.jobId?.title || 'Unknown Job';
};

const getUserName = (app: Application): string => {
  if (typeof app.userId === 'string') return 'Unknown User';
  return `${app.userId?.firstName || ''} ${app.userId?.lastName || ''}`.trim() || 'Unknown User';
};

const getUserEmail = (app: Application): string => {
  if (typeof app.userId === 'string') return '';
  return app.userId?.email || '';
};

const getUserProfession = (app: Application): string => {
  if (typeof app.userId === 'string') return '';
  const user = app.userId;
  if (user?.profession) {
    return user.specialization ? `${user.profession} - ${user.specialization}` : user.profession;
  }
  return '';
};

const getUserPhone = (app: Application): string => {
  if (typeof app.userId === 'string') return '';
  return app.userId?.phoneNumber || '';
};

const getUserCvUrl = (app: Application): string => {
  if (typeof app.userId === 'string') return '';
  return (app.userId as any)?.cvUrl || '';
};

const getUserEligibleToWork = (app: Application): boolean | undefined => {
  if (typeof app.userId === 'string') return undefined;
  return (app.userId as any)?.eligibleToWorkInUK;
};

const getUserAccreditation = (app: Application): string => {
  if (typeof app.userId === 'string') return '';
  return (app.userId as any)?.professionalAccreditation || '';
};

const getUserSpecialization = (app: Application): string => {
  if (typeof app.userId === 'string') return '';
  return (app.userId as any)?.specialization || '';
};

const getJobCompany = (app: Application): string => {
  if (typeof app.jobId === 'string') return '';
  return app.jobId?.company || '';
};

const getJobLocation = (app: Application): string => {
  if (typeof app.jobId === 'string') return '';
  return app.jobId?.location || '';
};

// Helper: compute days remaining until a deadline
const getDaysRemaining = (job: Job): number | null => {
  const deadline = job.applicationDeadline
    ? new Date(job.applicationDeadline)
    : job.postedDate && job.daysLeft
      ? new Date(new Date(job.postedDate).getTime() + job.daysLeft * 86400000)
      : null;
  if (!deadline) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  deadline.setHours(0, 0, 0, 0);
  return Math.ceil((deadline.getTime() - now.getTime()) / 86400000);
};

// Helper: format date to locale string
const fmtDate = (d?: string | Date | null): string => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Coalesce legacy statuses (under_review, interview_scheduled) into 'pending'.
const displayStatus = (status: string) =>
  status === 'accepted' || status === 'rejected' ? status : 'pending';

/* A state, marked rather than badged.

   The pills this replaces were bg-green-100 / bg-red-100 chips, which the
   design rules name specifically: the folio has one accent and no semantic
   colour ramp, so a state that is carried by hue alone cannot be told apart
   here at all. It is carried by geometry instead - a filled square for a
   settled state, a hollow one for a question still open - and the three
   inks say which kind of settled it is. */
const TONE: Record<string, { c: string; filled: boolean }> = {
  active:   { c: T.ink,    filled: true  },
  accepted: { c: T.ink,    filled: true  },
  pending:  { c: T.accent, filled: false },
  expired:  { c: T.accent, filled: false },
  closed:   { c: T.mute,   filled: true  },
  paused:   { c: T.mute,   filled: true  },
  rejected: { c: T.mute,   filled: true  },
};

const StatusMark: React.FC<{ status: string }> = ({ status }) => {
  const t = TONE[status] || TONE.closed;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, ...mono(T, t.c), fontSize: 10 }}>
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          background: t.filled ? t.c : 'transparent',
          border: `1.5px solid ${t.c}`,
          flexShrink: 0,
        }}
      />
      {status}
    </span>
  );
};

/* One head for every section of the dashboard, declared once. */
const SectionHead: React.FC<{
  num: string;
  eyebrow: string;
  title: string;
  action?: React.ReactNode;
}> = ({ num, eyebrow, title, action }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: 20,
      flexWrap: 'wrap',
      borderBottom: `1px solid ${pal.rule}`,
      paddingBottom: 13,
      marginBottom: 22,
    }}
  >
    <div>
      <div style={{ ...mono(T), fontSize: 10.5, letterSpacing: '0.22em' }}>
        § {num} · {eyebrow}
      </div>
      <h2
        style={{
          fontFamily: fam.sans,
          fontSize: 26,
          fontWeight: 600,
          letterSpacing: '-0.015em',
          margin: '6px 0 0',
          color: pal.ink,
        }}
      >
        {title}
      </h2>
    </div>
    {action}
  </div>
);

/* A quiet action in the margin of a row: mono, ruled under, never a pill. */
const rowAction = (tone: string): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'transparent',
  border: 'none',
  padding: '0 0 3px',
  cursor: 'pointer',
  ...mono(T, tone),
  fontSize: 9.5,
  letterSpacing: '0.16em',
  borderBottom: `1px solid ${pal.rule}`,
});

const metaItem: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  ...mono(T),
  fontSize: 9.5,
  letterSpacing: '0.14em',
};

/* A ruled schedule of label/value pairs - the sheet's own apparatus for
   particulars, used here for the candidate's. */
/* An em dash, not "Not provided". A blank in a schedule of particulars is a
   fact about the schedule, and it is read faster than a sentence apologising
   for itself on every empty row. */
const notStated = <span style={{ color: pal.inkMute }}>—</span>;

const INSTITUTION_TYPES = [
  { value: 'university', label: 'University' },
  { value: 'college', label: 'College' },
  { value: 'research-institute', label: 'Research institute' },
  { value: 'other', label: 'Other' },
];

const labelForType = (v?: string): React.ReactNode =>
  INSTITUTION_TYPES.find(t => t.value === v)?.label ?? notStated;

/* One switchable term of the account, ruled like a row of the register:
   what it is on the left, what it does about it on the right. */
const SettingRow: React.FC<{
  title: string;
  note: string;
  action: React.ReactNode;
  tone?: string;
}> = ({ title, note, action, tone }) => (
  <div
    className="ed-strip"
    style={{
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      gap: 20,
      alignItems: 'center',
      borderBottom: `1px solid ${pal.rule}`,
      padding: '16px 0',
    }}
  >
    <div>
      <div style={{ fontFamily: fam.sans, fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em', color: tone || pal.ink }}>
        {title}
      </div>
      <div style={{ fontFamily: fam.sans, fontSize: 13.5, lineHeight: 1.5, color: pal.inkSoft, marginTop: 4, maxWidth: '62ch' }}>
        {note}
      </div>
    </div>
    <div>{action}</div>
  </div>
);

const Facts: React.FC<{ rows: [string, React.ReactNode][] }> = ({ rows }) => (
  <dl style={{ margin: 0, border: `1px solid ${pal.rule}` }}>
    {rows.map(([k, v], i) => (
      <div
        key={k}
        className="ed-fact"
        style={{
          display: 'grid',
          gridTemplateColumns: '150px minmax(0, 1fr)',
          borderTop: i === 0 ? 'none' : `1px solid ${pal.rule}`,
        }}
      >
        <dt
          style={{
            ...label10,
            padding: '11px 13px',
            borderRight: `1px solid ${pal.rule}`,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {k}
        </dt>
        <dd
          style={{
            margin: 0,
            padding: '11px 13px',
            fontFamily: fam.sans,
            fontSize: 15,
            color: pal.ink,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          {v}
        </dd>
      </div>
    ))}
  </dl>
);

const DOSSIER_TITLE_ID = 'ed-dossier-title';

/* The candidate's dossier.

   Declared here rather than inside the page. As a nested component it was a
   new component type on every render of the dashboard, so React unmounted
   and remounted the whole dialog each time any parent state changed -
   taking focus and scroll position with it. */
const ApplicationSheet: React.FC<{
  app: Application;
  onStatus: (s: Application['status']) => void;
}> = ({ app, onStatus }) => {
  const status = displayStatus(app.status);
  const eligible = getUserEligibleToWork(app);
  const cv = app.resumeUrl || getUserCvUrl(app);
  const letterIsFile = !!app.coverLetter && app.coverLetter.startsWith('http');

  return (
    <div style={{ padding: '30px 32px 32px', display: 'grid', gap: 30 }}>
      <div>
        <div style={{ ...mono(T, T.accent), marginBottom: 10 }}>§ Candidate</div>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <h2
            id={DOSSIER_TITLE_ID}
            style={{
              fontFamily: fam.sans,
              fontSize: 'clamp(26px, 3.4vw, 34px)',
              fontWeight: 600,
              letterSpacing: '-0.025em',
              lineHeight: 1.02,
              margin: 0,
              color: pal.ink,
            }}
          >
            {getUserName(app)}
            <span style={{ color: pal.accent }}>.</span>
          </h2>
          <StatusMark status={status} />
        </div>
        <div style={{ ...mono(T), fontSize: 9.5, letterSpacing: '0.16em', marginTop: 10 }}>
          Applied {fmtDate(app.appliedAt)}
        </div>
      </div>

      <Facts
        rows={[
          ['Position', <span key="p" style={{ fontWeight: 600 }}>{getJobTitle(app)}</span>],
          ...((getJobCompany(app) || getJobLocation(app))
            ? ([['Institution', [getJobCompany(app), getJobLocation(app)].filter(Boolean).join(' · ')]] as [string, React.ReactNode][])
            : []),
          ['Email', <a key="e" href={`mailto:${getUserEmail(app)}`} className="ed-link" style={{ color: pal.ink }}>{getUserEmail(app)}</a>],
          ...(getUserPhone(app)
            ? ([['Phone', <a key="t" href={`tel:${getUserPhone(app)}`} className="ed-link" style={{ color: pal.ink }}>{getUserPhone(app)}</a>]] as [string, React.ReactNode][])
            : []),
          ...(getUserProfession(app) ? ([['Background', getUserProfession(app)]] as [string, React.ReactNode][]) : []),
          ...(getUserSpecialization(app) ? ([['Specialisation', getUserSpecialization(app)]] as [string, React.ReactNode][]) : []),
          ...(getUserAccreditation(app) ? ([['HE recognition', getUserAccreditation(app)]] as [string, React.ReactNode][]) : []),
          ['Availability', app.availability],
          ...(app.expectedSalary ? ([['Expected salary', app.expectedSalary]] as [string, React.ReactNode][]) : []),
          [
            'Right to work',
            <StatusMark
              key="r"
              status={eligible === true ? 'active' : eligible === false ? 'rejected' : 'closed'}
            />,
          ],
          [
            'CV',
            cv ? (
              <button
                type="button"
                onClick={() => downloadFile(cv, `${getUserName(app)}-CV`)}
                className="ed-quiet"
                style={rowAction(pal.ink)}
              >
                <Download size={ICON} strokeWidth={1.5} /> Download
              </button>
            ) : (
              <span style={{ ...mono(T) }}>Not uploaded</span>
            ),
          ],
        ]}
      />

      <div>
        <div style={{ ...label10, marginBottom: 10 }}>Cover letter</div>
        {letterIsFile ? (
          <button
            type="button"
            onClick={() => downloadFile(app.coverLetter, `${getUserName(app)}-CoverLetter`)}
            className="ed-quiet"
            style={rowAction(pal.ink)}
          >
            <Download size={ICON} strokeWidth={1.5} /> Download cover letter
          </button>
        ) : (
          <p
            style={{
              margin: 0,
              border: `1px solid ${pal.rule}`,
              padding: '14px 16px',
              fontFamily: fam.sans,
              fontSize: 15,
              lineHeight: 1.6,
              color: pal.inkSoft,
              whiteSpace: 'pre-wrap',
            }}
          >
            {app.coverLetter || 'No cover letter provided.'}
          </p>
        )}
      </div>

      {app.additionalInfo && (
        <div>
          <div style={{ ...label10, marginBottom: 10 }}>Additional information</div>
          <p
            style={{
              margin: 0,
              border: `1px solid ${pal.rule}`,
              padding: '14px 16px',
              fontFamily: fam.sans,
              fontSize: 15,
              lineHeight: 1.6,
              color: pal.inkSoft,
              whiteSpace: 'pre-wrap',
            }}
          >
            {app.additionalInfo}
          </p>
        </div>
      )}

      {/* The decision closes the sheet, under a heavy rule. */}
      <div style={{ borderTop: `1.5px solid ${pal.ink}`, paddingTop: 20 }}>
        {status === 'pending' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => onStatus('accepted')} className="ed-accent" style={btn(T, 'accent')}>
              Accept
            </button>
            <button type="button" onClick={() => onStatus('rejected')} className="ed-out" style={btn(T, 'outline')}>
              Reject
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={mono(T)}>Decision recorded</span>
            <StatusMark status={status} />
          </div>
        )}
      </div>
    </div>
  );
};

const EmployerDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToast } = useAppStore();

  // Read initial tab from URL query parameter
  const initialTab = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [editingDeadlineJobId, setEditingDeadlineJobId] = useState<string | null>(null);
  const [newDeadline, setNewDeadline] = useState('');

  /* Profile and settings. The particulars are whatever the institution
     entered at registration and onboarding, read back from /auth/me - the
     same source the seeker's desk reads - rather than a second copy kept
     here that could drift from it. */
  const { getProfile, updateProfile } = useUser();
  const [profileData, setProfileData] = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState<Partial<EmployerProfile>>({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileVisible, setProfileVisible] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Update active tab when URL changes
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && ['overview', 'jobs', 'applications', 'profile', 'settings'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  /* Fetched when one of the two panels that needs it is first opened, not on
     every visit to the desk: most visits are for the listings. */
  useEffect(() => {
    if (activeTab !== 'profile' && activeTab !== 'settings') return;
    if (profileData || profileLoading) return;
    let cancelled = false;
    (async () => {
      try {
        setProfileLoading(true);
        const data = await getProfile();
        if (cancelled) return;
        const p = data?.user || data;
        setProfileData(p);
        if (typeof p?.profileVisible === 'boolean') setProfileVisible(p.profileVisible);
      } catch {
        if (!cancelled) {
          addToast({ type: 'error', title: 'Could not load', message: 'Your particulars could not be read.' });
        }
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const startEditProfile = () => {
    setProfileForm({
      uniName: profileData?.uniName || '',
      institutionType: profileData?.institutionType,
      institutionWebsite: profileData?.institutionWebsite || '',
      address: profileData?.address || '',
      contactPerson: profileData?.contactPerson || '',
      phoneNumber: profileData?.phoneNumber || '',
      institutionDescription: profileData?.institutionDescription || '',
    });
    setIsEditingProfile(true);
  };

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      await updateProfile(profileForm);
      setProfileData((prev: any) => ({ ...(prev || {}), ...profileForm }));
      setIsEditingProfile(false);
      addToast({ type: 'success', title: 'Particulars saved', message: 'Your institution details have been updated.' });
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Save failed',
        message: err instanceof Error ? err.message : 'Could not save your details.',
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleToggleVisibility = async () => {
    const next = !profileVisible;
    try {
      await api.put(`/auth/updateUser/${user?.id}`, { profileVisible: next });
      setProfileVisible(next);
      addToast({
        type: 'success',
        title: 'Visibility updated',
        message: next ? 'Your institution is listed in search.' : 'Your institution is hidden from search.',
      });
    } catch {
      addToast({ type: 'error', title: 'Update failed', message: 'Could not change visibility.' });
    }
  };

  const handleExportData = async () => {
    try {
      setIsExporting(true);
      const response = await api.get('/auth/export-data');
      const data = await response.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `acanetic-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      addToast({ type: 'success', title: 'Export complete', message: 'Your data has been downloaded.' });
    } catch {
      addToast({ type: 'error', title: 'Export failed', message: 'Could not export your data.' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      addToast({ type: 'error', title: 'Password required', message: 'Enter your password to confirm.' });
      return;
    }
    try {
      setIsDeleting(true);
      await api.delete('/auth/account', { password: deletePassword });
      addToast({ type: 'success', title: 'Account deleted', message: 'Your account has been permanently deleted.' });
      navigate(ROUTE_NAMES.PAGES.HOME);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Delete failed',
        message: err instanceof Error ? err.message : 'Could not delete your account.',
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteAccount(false);
      setDeletePassword('');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch jobs and applications in parallel
        const [jobsData, applicationsData] = await Promise.all([
          fetchJobsByEmployer(user.id),
          getEmployerApplications(),
        ]);

        setJobs(jobsData);
        setApplications(applicationsData);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.id]);

  /* The tab was read from the query on the way in but never written back on
     the way round, so a reload or a back button dropped the reader onto the
     overview from wherever they actually were. */
  const selectTab = (id: string) => {
    setActiveTab(id);
    setSearchParams(id === 'overview' ? {} : { tab: id }, { replace: true });
  };

  const handleDeleteJob = async (jobId: string) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;

    try {
      await deleteJob(jobId);
      setJobs(jobs.filter(job => job.id !== jobId));
      addToast({
        type: 'success',
        title: 'Listing withdrawn',
        message: 'The listing has been removed from the register.',
      });
    } catch (err) {
      console.error('Error deleting job:', err);
      addToast({
        type: 'error',
        title: 'Withdrawal failed',
        message: 'The listing could not be removed. Try again in a moment.',
      });
    }
  };

  const handleUpdateDeadline = async (jobId: string) => {
    if (!newDeadline) return;
    try {
      const updated = await updateJob(jobId, {
        applicationDeadline: newDeadline,
        status: 'active',
      } as any);
      setJobs(jobs.map(j => j.id === jobId ? { ...j, applicationDeadline: updated.applicationDeadline, status: 'active' } : j));
      setEditingDeadlineJobId(null);
      setNewDeadline('');
      addToast({
        type: 'success',
        title: 'Closing date set',
        message: 'The listing is open again and carries its new closing date.',
      });
    } catch (err) {
      console.error('Error updating deadline:', err);
      addToast({
        type: 'error',
        title: 'Update failed',
        message: 'The closing date could not be changed. Try again in a moment.',
      });
    }
  };

  const handleUpdateApplicationStatus = async (
    applicationId: string,
    newStatus: Application['status']
  ) => {
    try {
      await updateApplicationStatus(applicationId, newStatus);
      setApplications(applications.map(app =>
        app._id === applicationId ? { ...app, status: newStatus } : app
      ));
      setSelectedApplication(prev =>
        prev && prev._id === applicationId ? { ...prev, status: newStatus } : prev
      );
      const statusLabels: Record<Application['status'], string> = {
        pending: 'Pending',
        accepted: 'Accepted',
        rejected: 'Rejected',
      };
      addToast({
        type: 'success',
        title: 'Decision recorded',
        message: `The application is now marked "${statusLabels[newStatus]}".`,
      });
    } catch (err) {
      console.error('Error updating application status:', err);
      addToast({
        type: 'error',
        title: 'Update failed',
        message: 'The decision could not be recorded. Try again in a moment.',
      });
    }
  };

  const stats = {
    totalJobs: jobs.length,
    activeJobs: jobs.filter(job => job.status === 'active').length,
    totalApplications: applications.length,
    newApplications: applications.filter(app => app.status === 'pending').length,
  };

  const tabs = [
    { id: 'overview', label: 'Overview', index: '01' },
    { id: 'jobs', label: 'Listings', index: '02' },
    { id: 'applications', label: 'Applications', index: '03' },
    { id: 'profile', label: 'Profile', index: '04' },
    { id: 'settings', label: 'Settings', index: '05' },
  ];

  if (!user || user.role !== 'employer') {
    return (
      <SheetGround
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 20,
          textAlign: 'center',
          padding: '40px 20px',
          fontFamily: fam.sans,
        }}
      >
        <div style={{ ...mono(T, T.accent), letterSpacing: '0.22em' }}>§ Not your desk</div>
        <h1
          style={{
            fontFamily: fam.sans,
            fontSize: 'clamp(30px, 5vw, 52px)',
            fontWeight: 600,
            letterSpacing: '-0.03em',
            lineHeight: 1,
            color: pal.ink,
            margin: 0,
          }}
        >
          This desk is for institutions.
        </h1>
        <p style={{ fontFamily: fam.sans, fontSize: 17, color: pal.inkSoft, maxWidth: 460, margin: 0 }}>
          You are signed in as a scholar. The register is where your side of it lives.
        </p>
        <button type="button" onClick={() => navigate(ROUTE_NAMES.PAGES.JOBS)} className="ed-accent" style={btn(T, 'accent')}>
          Back to the register
        </button>
      </SheetGround>
    );
  }

  if (loading) {
    return <SheetLoading text="Loading your listings…" />;
  }

  if (error) {
    return (
      <SheetGround
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 20,
          textAlign: 'center',
          padding: '40px 20px',
          fontFamily: fam.sans,
        }}
      >
        <div style={{ ...mono(T, T.accent), letterSpacing: '0.22em' }}>§ Not loaded</div>
        <h1
          style={{
            fontFamily: fam.sans,
            fontSize: 'clamp(30px, 5vw, 52px)',
            fontWeight: 600,
            letterSpacing: '-0.03em',
            lineHeight: 1,
            color: pal.ink,
            margin: 0,
          }}
        >
          Your desk could not be drawn.
        </h1>
        <p style={{ fontFamily: fam.sans, fontSize: 17, color: pal.inkSoft, maxWidth: 460, margin: 0 }}>
          {error}
        </p>
        <button type="button" onClick={() => window.location.reload()} className="ed-accent" style={btn(T, 'accent')}>
          Try again
        </button>
      </SheetGround>
    );
  }

  /* A listing, ruled as a row. Used by both the overview extract and the
     full schedule, so the two cannot describe the same listing differently. */
  const JobRow: React.FC<{ job: Job; full?: boolean }> = ({ job, full }) => {
    const daysLeft = getDaysRemaining(job);
    const isExpired = daysLeft !== null && daysLeft <= 0;
    const isUrgent = daysLeft !== null && daysLeft > 0 && daysLeft <= 3;
    const appCount = applications.filter(a => getJobId(a) === job.id).length;
    const status = isExpired ? 'expired' : (job.status || 'active');

    return (
      <div style={{ borderBottom: `1px solid ${pal.rule}`, padding: '18px 0' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0, flex: '1 1 340px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap', marginBottom: 6 }}>
              <h3
                style={{
                  fontFamily: fam.sans,
                  fontSize: 18,
                  fontWeight: 600,
                  letterSpacing: '-0.015em',
                  color: pal.ink,
                  margin: 0,
                }}
              >
                {job.title}
              </h3>
              <StatusMark status={status} />
              {isUrgent && (
                <span style={{ ...mono(T, T.accent), fontSize: 9.5, letterSpacing: '0.16em' }}>
                  {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
                </span>
              )}
            </div>
            <div style={{ ...mono(T), fontSize: 10, letterSpacing: '0.16em', marginBottom: 10 }}>
              {job.location}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18 }}>
              {full && (
                <span style={metaItem}>
                  <Calendar size={ICON} strokeWidth={1.5} /> Posted {fmtDate(job.postedDate)}
                </span>
              )}
              <span style={metaItem}>
                <AcaIcon name="clock" size={ICON} stroke={pal.inkSoft} />
                {job.applicationDeadline
                  ? `Closes ${fmtDate(job.applicationDeadline)}`
                  : job.daysLeft
                    ? `Closes in ${job.daysLeft} days`
                    : 'No closing date'}
              </span>
              <span style={metaItem}>
                <Eye size={ICON} strokeWidth={1.5} /> {job.viewCount ?? 0} views
              </span>
              <span style={metaItem}>
                <User size={ICON} strokeWidth={1.5} /> {appCount} application{appCount !== 1 ? 's' : ''}
              </span>
              {full && job.jobCategory && (
                <span style={metaItem}>
                  <Briefcase size={ICON} strokeWidth={1.5} /> {job.jobCategory}
                </span>
              )}
            </div>

            {editingDeadlineJobId === job.id && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 16 }}>
                <input
                  type="date"
                  value={newDeadline}
                  onChange={e => setNewDeadline(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  aria-label="New closing date"
                  style={{
                    background: 'transparent',
                    border: `1.5px solid ${pal.ink}`,
                    borderRadius: 0,
                    padding: '9px 12px',
                    fontFamily: fam.mono,
                    fontSize: 13,
                    color: pal.ink,
                    outline: 'none',
                  }}
                />
                <button type="button" onClick={() => handleUpdateDeadline(job.id)} className="ed-accent" style={{ ...btn(T, 'accent'), padding: '10px 18px' }}>
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => { setEditingDeadlineJobId(null); setNewDeadline(''); }}
                  className="ed-quiet"
                  style={rowAction(pal.inkSoft)}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', flexShrink: 0 }}>
            <button type="button" onClick={() => navigate(`/jobs/${job.id}`)} className="ed-quiet" style={rowAction(pal.ink)}>
              View
            </button>
            <button type="button" onClick={() => navigate(`/edit-job/${job.id}`)} className="ed-quiet" style={rowAction(pal.inkSoft)}>
              <Pencil size={ICON} strokeWidth={1.5} /> Edit
            </button>
            <button
              type="button"
              onClick={() => {
                setEditingDeadlineJobId(job.id);
                setNewDeadline(job.applicationDeadline ? new Date(job.applicationDeadline).toISOString().split('T')[0] : '');
              }}
              className="ed-quiet"
              style={rowAction(pal.inkSoft)}
            >
              {isExpired ? 'Reactivate' : 'Extend'}
            </button>
            <button type="button" onClick={() => handleDeleteJob(job.id)} className="ed-quiet ed-danger" style={rowAction(pal.accent)}>
              <Trash2 size={ICON} strokeWidth={1.5} /> Withdraw
            </button>
          </div>
        </div>
      </div>
    );
  };

  const ApplicationRow: React.FC<{ app: Application; full?: boolean }> = ({ app, full }) => (
    <div style={{ borderBottom: `1px solid ${pal.rule}`, padding: '18px 0' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: '1 1 340px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap', marginBottom: 6 }}>
            <h3
              style={{
                fontFamily: fam.sans,
                fontSize: 18,
                fontWeight: 600,
                letterSpacing: '-0.015em',
                color: pal.ink,
                margin: 0,
              }}
            >
              {getUserName(app)}
            </h3>
            <StatusMark status={displayStatus(app.status)} />
          </div>
          <div style={{ fontFamily: fam.sans, fontSize: 15, color: pal.inkSoft, marginBottom: 10 }}>
            {getJobTitle(app)}
            {getUserProfession(app) ? ` · ${getUserProfession(app)}` : ''}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18 }}>
            <span style={metaItem}>
              <AcaIcon name="mail" size={ICON} stroke={pal.inkSoft} /> {getUserEmail(app)}
            </span>
            <span style={metaItem}>
              <AcaIcon name="clock" size={ICON} stroke={pal.inkSoft} /> {app.availability}
            </span>
            {app.expectedSalary && (
              <span style={metaItem}>
                <PoundSterling size={ICON} strokeWidth={1.5} /> {app.expectedSalary}
              </span>
            )}
            {full && (
              <span style={metaItem}>
                <Calendar size={ICON} strokeWidth={1.5} /> Applied {fmtDate(app.appliedAt)}
              </span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', flexShrink: 0 }}>
          <button type="button" onClick={() => setSelectedApplication(app)} className="ed-quiet" style={rowAction(pal.ink)}>
            Open dossier
          </button>
          {app.resumeUrl && (
            <button
              type="button"
              onClick={() => downloadFile(app.resumeUrl!, `${getUserName(app)}-Resume`)}
              className="ed-quiet"
              style={rowAction(pal.inkSoft)}
            >
              <Download size={ICON} strokeWidth={1.5} /> CV
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const Empty: React.FC<{ line: string; cta?: React.ReactNode }> = ({ line, cta }) => (
    <div style={{ padding: '40px 0', textAlign: 'center', display: 'grid', gap: 16, justifyItems: 'center' }}>
      <p style={{ fontFamily: fam.sans, fontSize: 16, color: pal.inkSoft, margin: 0 }}>{line}</p>
      {cta}
    </div>
  );

  return (
    <SheetGround className="ed-root" style={{ color: pal.ink, fontFamily: fam.sans, minHeight: '100vh' }}>
      {/* Masthead */}
      <section style={{ borderBottom: `1px solid ${pal.rule}` }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '40px 20px 40px' }}>
          <div
            className="ed-strip"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 16,
              borderBottom: `1px solid ${pal.rule}`,
              paddingBottom: 13,
              marginBottom: 28,
              ...label10,
            }}
          >
            <div>§ Desk · Institution</div>
            <div style={{ textAlign: 'center' }}>{(user as any)?.uniName || user?.email || ''}</div>
            <div style={{ textAlign: 'right' }}>
              {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
            <h1
              style={{
                fontFamily: fam.sans,
                fontSize: 'clamp(34px, 5vw, 58px)',
                lineHeight: 0.98,
                letterSpacing: '-0.035em',
                fontWeight: 600,
                margin: 0,
                color: pal.ink,
                maxWidth: 620,
              }}
            >
              Your listings and candidates
              <span style={{ color: pal.accent }}>.</span>
            </h1>
            <button
              type="button"
              onClick={() => navigate(ROUTE_NAMES.PAGES.POST_JOB)}
              className="ed-accent"
              style={btn(T, 'accent')}
            >
              Post a listing
            </button>
          </div>
        </div>
      </section>

      {/* Body — index rail + the desk itself */}
      <section>
        <div
          className="ed-body"
          style={{
            maxWidth: 1280,
            margin: '0 auto',
            padding: '48px 20px 84px',
            display: 'grid',
            gridTemplateColumns: '196px minmax(0, 1fr)',
            gap: 56,
            alignItems: 'start',
          }}
        >
          <nav
            aria-label="Sections of your desk"
            className="ed-index"
            style={{
              alignSelf: 'start',
              position: 'sticky',
              top: 104,
              borderRight: `1px solid ${pal.rule}`,
              paddingRight: 24,
            }}
          >
            <div style={{ ...mono(T, T.accent), marginBottom: 14 }} className="ed-index-head">
              § Contents
            </div>
            <ol className="ed-index-list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {tabs.map(tab => {
                const active = activeTab === tab.id;
                return (
                  <li key={tab.id} className="ed-index-item" style={{ borderTop: `1px solid ${pal.rule}` }}>
                    <button
                      type="button"
                      onClick={() => selectTab(tab.id)}
                      aria-current={active ? 'true' : undefined}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        background: 'transparent',
                        border: 'none',
                        borderLeft: `2px solid ${active ? pal.accent : 'transparent'}`,
                        padding: '11px 0 11px 12px',
                        cursor: 'pointer',
                        ...mono(T),
                        fontSize: 10,
                        letterSpacing: '0.16em',
                        color: active ? pal.ink : pal.inkSoft,
                      }}
                    >
                      <span style={{ color: active ? pal.accent : pal.inkMute, marginRight: 8 }}>{tab.index}</span>
                      {tab.label}
                    </button>
                  </li>
                );
              })}

            </ol>
          </nav>

          <div style={{ minWidth: 0 }}>
            {activeTab === 'overview' && (
              <div style={{ display: 'grid', gap: 52 }}>
                {/* The count, ruled into one block rather than four boxes. */}
                <div className="ed-stat-clip" style={{ border: `1.5px solid ${pal.ink}`, background: pal.paper }}>
                  <div className="ed-stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
                    {([
                      { idx: '01', label: 'Listings', value: stats.totalJobs, to: 'jobs' },
                      { idx: '02', label: 'Open', value: stats.activeJobs, to: 'jobs' },
                      { idx: '03', label: 'Applications', value: stats.totalApplications, to: 'applications' },
                      /* The only figure that is a request rather than a
                         record, so the only one allowed to raise its voice. */
                      { idx: '04', label: 'To review', value: stats.newApplications, to: 'applications', mark: true },
                    ] as { idx: string; label: string; value: number; to: string; mark?: boolean }[]).map(s => (
                      <button
                        key={s.idx}
                        type="button"
                        onClick={() => selectTab(s.to)}
                        className="ed-stat"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          textAlign: 'left',
                          padding: '16px 18px 18px',
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ ...label10, marginBottom: 14 }}>
                          § {s.idx} · {s.label}
                        </div>
                        <div
                          style={{
                            fontFamily: fam.sans,
                            fontSize: 44,
                            fontWeight: 600,
                            letterSpacing: '-0.03em',
                            lineHeight: 1,
                            color: s.mark && s.value > 0 ? pal.accent : pal.ink,
                          }}
                        >
                          {s.value}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <SectionHead
                    num="01"
                    eyebrow="Listings"
                    title="Most recent."
                    action={
                      <button type="button" onClick={() => selectTab('jobs')} className="ed-quiet" style={rowAction(pal.ink)}>
                        All listings →
                      </button>
                    }
                  />
                  {jobs.length === 0 ? (
                    <Empty
                      line="Nothing posted to the register yet."
                      cta={
                        <button type="button" onClick={() => navigate(ROUTE_NAMES.PAGES.POST_JOB)} className="ed-accent" style={btn(T, 'accent')}>
                          Post your first listing
                        </button>
                      }
                    />
                  ) : (
                    <div style={{ borderTop: `1px solid ${pal.rule}` }}>
                      {jobs.slice(0, 3).map(job => <JobRow key={job.id} job={job} />)}
                    </div>
                  )}
                </div>

                <div>
                  <SectionHead
                    num="02"
                    eyebrow="Applications"
                    title="Most recent."
                    action={
                      <button type="button" onClick={() => selectTab('applications')} className="ed-quiet" style={rowAction(pal.ink)}>
                        All applications →
                      </button>
                    }
                  />
                  {applications.length === 0 ? (
                    <Empty line="No one has applied yet." />
                  ) : (
                    <div style={{ borderTop: `1px solid ${pal.rule}` }}>
                      {applications.slice(0, 3).map(app => <ApplicationRow key={app._id} app={app} />)}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'jobs' && (
              <div>
                <SectionHead
                  num="02"
                  eyebrow="Listings"
                  title="Everything you have posted."
                  action={
                    <button type="button" onClick={() => navigate(ROUTE_NAMES.PAGES.POST_JOB)} className="ed-accent" style={btn(T, 'accent')}>
                      Post a listing
                    </button>
                  }
                />
                {jobs.length === 0 ? (
                  <Empty
                    line="Nothing posted to the register yet."
                    cta={
                      <button type="button" onClick={() => navigate(ROUTE_NAMES.PAGES.POST_JOB)} className="ed-accent" style={btn(T, 'accent')}>
                        Post your first listing
                      </button>
                    }
                  />
                ) : (
                  <div style={{ borderTop: `1px solid ${pal.rule}` }}>
                    {jobs.map(job => <JobRow key={job.id} job={job} full />)}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'applications' && (
              <div>
                <SectionHead num="03" eyebrow="Applications" title="Everyone who has applied." />
                {applications.length === 0 ? (
                  <Empty line="No one has applied yet. Applications appear here as they arrive." />
                ) : (
                  <div style={{ borderTop: `1px solid ${pal.rule}` }}>
                    {applications.map(app => <ApplicationRow key={app._id} app={app} full />)}
                  </div>
                )}
              </div>
            )}

            {/* § 04 · Profile ─────────────────────────────────────────
                What the institution entered when it registered and was
                onboarded, read back rather than re-asked. At rest it is a
                schedule of facts; editing turns the same rows into a title
                block, so the reading order never changes under the hand. */}
            {activeTab === 'profile' && (
              <div>
                <SectionHead
                  num="04"
                  eyebrow="Profile"
                  title="Your particulars."
                  action={
                    !profileLoading && (
                      isEditingProfile ? (
                        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            onClick={() => setIsEditingProfile(false)}
                            className="ed-quiet"
                            style={rowAction(pal.inkSoft)}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleSaveProfile}
                            disabled={savingProfile}
                            className="ed-accent"
                            style={{ ...btn(T, 'accent'), padding: '11px 18px', fontSize: 10.5, opacity: savingProfile ? 0.55 : 1 }}
                          >
                            {savingProfile ? 'Saving…' : 'Save changes'}
                          </button>
                        </div>
                      ) : (
                        <button type="button" onClick={startEditProfile} className="ed-quiet" style={rowAction(pal.ink)}>
                          Edit particulars
                        </button>
                      )
                    )
                  }
                />

                {profileLoading ? (
                  <div style={{ padding: '48px 0', display: 'flex', justifyContent: 'center' }}>
                    <LoadingSpinner size="md" text="Reading your particulars…" />
                  </div>
                ) : isEditingProfile ? (
                  <FieldBlock>
                    <FieldRow label="Institution" htmlFor="ed-uniName" required first>
                      <input
                        id="ed-uniName"
                        value={profileForm.uniName || ''}
                        onChange={e => setProfileForm(p => ({ ...p, uniName: e.target.value }))}
                        style={fieldInput}
                        placeholder="University of Cambridge"
                      />
                    </FieldRow>
                    <FieldRow label="Type" htmlFor="ed-institutionType">
                      <FolioSelect
                        bare
                        id="ed-institutionType"
                        placeholder="Not stated"
                        value={profileForm.institutionType || ''}
                        onChange={v => setProfileForm(p => ({ ...p, institutionType: v as EmployerProfile['institutionType'] }))}
                        options={INSTITUTION_TYPES}
                      />
                    </FieldRow>
                    <FieldRow label="Website" htmlFor="ed-website">
                      <input
                        id="ed-website"
                        type="url"
                        value={profileForm.institutionWebsite || ''}
                        onChange={e => setProfileForm(p => ({ ...p, institutionWebsite: e.target.value }))}
                        style={fieldInput}
                        placeholder="https://example.ac.uk"
                      />
                    </FieldRow>
                    <FieldRow label="Address" htmlFor="ed-address" stacked>
                      <textarea
                        id="ed-address"
                        rows={3}
                        value={profileForm.address || ''}
                        onChange={e => setProfileForm(p => ({ ...p, address: e.target.value }))}
                        style={fieldArea}
                        placeholder="Full address"
                      />
                    </FieldRow>
                    <FieldRow label="Contact" htmlFor="ed-contactPerson">
                      <input
                        id="ed-contactPerson"
                        value={profileForm.contactPerson || ''}
                        onChange={e => setProfileForm(p => ({ ...p, contactPerson: e.target.value }))}
                        style={fieldInput}
                        placeholder="Dr. Eleanor Marsh"
                      />
                    </FieldRow>
                    <FieldRow label="Phone" htmlFor="ed-phone">
                      <input
                        id="ed-phone"
                        type="tel"
                        value={profileForm.phoneNumber || ''}
                        onChange={e => setProfileForm(p => ({ ...p, phoneNumber: e.target.value }))}
                        style={fieldInput}
                        placeholder="+44 1234 567890"
                      />
                    </FieldRow>
                    <FieldRow label="About" htmlFor="ed-description" stacked>
                      <textarea
                        id="ed-description"
                        rows={5}
                        value={profileForm.institutionDescription || ''}
                        onChange={e => setProfileForm(p => ({ ...p, institutionDescription: e.target.value }))}
                        style={fieldArea}
                        placeholder="What the institution is, and what it is like to work at."
                      />
                    </FieldRow>
                  </FieldBlock>
                ) : (
                  <>
                    <Facts
                      rows={[
                        ['Institution', profileData?.uniName || notStated],
                        ['Type', labelForType(profileData?.institutionType)],
                        [
                          'Website',
                          profileData?.institutionWebsite ? (
                            <a
                              href={profileData.institutionWebsite}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ed-link"
                              style={{ color: pal.ink }}
                            >
                              {profileData.institutionWebsite}
                            </a>
                          ) : notStated,
                        ],
                        ['Address', profileData?.address || notStated],
                        ['Contact', profileData?.contactPerson || notStated],
                        ['Phone', profileData?.phoneNumber || notStated],
                        ['Account', profileData?.email || user?.email || notStated],
                        [
                          'Registered',
                          profileData?.createdAt
                            ? new Date(profileData.createdAt).toLocaleDateString()
                            : notStated,
                        ],
                      ]}
                    />

                    {profileData?.institutionDescription && (
                      <div style={{ marginTop: 34 }}>
                        <div style={{ ...label10, marginBottom: 10 }}>About the institution</div>
                        <p style={{ fontFamily: fam.sans, fontSize: 16, lineHeight: 1.6, color: pal.ink, margin: 0, maxWidth: '68ch', whiteSpace: 'pre-wrap' }}>
                          {profileData.institutionDescription}
                        </p>
                      </div>
                    )}

                    {Array.isArray(profileData?.institutionCulture) && profileData.institutionCulture.length > 0 && (
                      <div style={{ marginTop: 30 }}>
                        <div style={{ ...label10, marginBottom: 10 }}>Culture</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {profileData.institutionCulture.map((c: string) => (
                            <span
                              key={c}
                              style={{
                                ...mono(T, pal.onAccent),
                                background: pal.ink,
                                padding: '5px 9px',
                                fontSize: 10,
                                letterSpacing: '0.1em',
                              }}
                            >
                              {c}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* § 05 · Settings ────────────────────────────────────────
                Only what actually persists. The old profile page also
                offered three e-mail toggles that were defaultChecked with no
                handler and no endpoint behind them - a switch that forgets
                is worse than no switch, so they are not carried over. */}
            {activeTab === 'settings' && (
              <div>
                <SectionHead num="05" eyebrow="Settings" title="Account & data." />

                <Facts
                  rows={[
                    ['Signed in as', profileData?.email || user?.email || notStated],
                    ['Role', 'Institution'],
                  ]}
                />

                <div style={{ marginTop: 40 }}>
                  <div style={{ ...label10, marginBottom: 14 }}>Privacy & data</div>

                  <div style={{ borderTop: `1px solid ${pal.rule}` }}>
                    <SettingRow
                      title="Listed in search"
                      note={profileVisible
                        ? 'Your institution appears in search and on its public profile.'
                        : 'Your institution is hidden from search. Live listings stay visible.'}
                      action={
                        <button
                          type="button"
                          onClick={handleToggleVisibility}
                          aria-pressed={profileVisible}
                          className="ed-out"
                          style={{
                            ...btn(T, profileVisible ? 'invert' : 'outline'),
                            padding: '9px 14px',
                            fontSize: 10,
                          }}
                        >
                          {profileVisible ? 'Listed' : 'Hidden'}
                        </button>
                      }
                    />
                    <SettingRow
                      title="Export my data"
                      note="Download everything held about this account as a JSON file."
                      action={
                        <button
                          type="button"
                          onClick={handleExportData}
                          disabled={isExporting}
                          className="ed-out"
                          style={{ ...btn(T, 'outline'), padding: '9px 14px', fontSize: 10, opacity: isExporting ? 0.55 : 1 }}
                        >
                          {isExporting ? 'Exporting…' : 'Export'}
                        </button>
                      }
                    />
                    <SettingRow
                      title="Delete this account"
                      note="Permanently deletes the account, its listings and every application to them. This cannot be undone."
                      tone={pal.accent}
                      action={
                        !showDeleteAccount && (
                          <button
                            type="button"
                            onClick={() => setShowDeleteAccount(true)}
                            className="ed-danger"
                            style={{ ...btn(T, 'outline'), padding: '9px 14px', fontSize: 10, borderColor: pal.accent, color: pal.accent }}
                          >
                            Delete
                          </button>
                        )
                      }
                    />
                  </div>

                  {/* The confirmation is on the sheet rather than in a dialog:
                      it is read where the consequence is written, and the
                      password makes the act deliberate on its own. */}
                  {showDeleteAccount && (
                    <div style={{ border: `1.5px solid ${pal.accent}`, padding: 20, marginTop: 20 }}>
                      <div style={{ ...mono(T, pal.accent), marginBottom: 10 }}>§ Confirm deletion</div>
                      <p style={{ fontFamily: fam.sans, fontSize: 14.5, lineHeight: 1.5, color: pal.ink, margin: '0 0 16px', maxWidth: '60ch' }}>
                        This is irreversible. Your profile, listings, applications and uploaded files are all
                        permanently deleted. Enter your password to confirm.
                      </p>
                      <FieldBlock>
                        <FieldRow label="Password" htmlFor="ed-delete-password" required first>
                          <input
                            id="ed-delete-password"
                            type="password"
                            value={deletePassword}
                            onChange={e => setDeletePassword(e.target.value)}
                            style={fieldInput}
                            placeholder="Your password"
                            autoComplete="current-password"
                          />
                        </FieldRow>
                      </FieldBlock>
                      <div style={{ display: 'flex', gap: 18, alignItems: 'center', marginTop: 18, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={handleDeleteAccount}
                          disabled={isDeleting}
                          style={{
                            ...btn(T, 'accent'),
                            padding: '11px 18px',
                            fontSize: 10.5,
                            opacity: isDeleting ? 0.55 : 1,
                            cursor: isDeleting ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {isDeleting ? 'Deleting…' : 'Delete permanently'}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setShowDeleteAccount(false); setDeletePassword(''); }}
                          className="ed-quiet"
                          style={rowAction(pal.inkSoft)}
                        >
                          Keep my account
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* The dossier, on the site's own dialog - which owns the focus trap,
          the Escape key, the scroll lock and aria-modal. The hand-rolled
          overlay this replaces had none of them. */}
      <AuthModal
        isOpen={!!selectedApplication}
        onClose={() => setSelectedApplication(null)}
        size="lg"
        labelledBy={DOSSIER_TITLE_ID}
      >
        {selectedApplication && (
          <ApplicationSheet
            app={selectedApplication}
            onStatus={(s) => handleUpdateApplicationStatus(selectedApplication._id, s)}
          />
        )}
      </AuthModal>

      <style>{`
        ${folioFormCss}

        /* The count block rules its own cells; the grid is hung 1px past the
           box and clipped, so the outer rules land under the sheet's border
           at any column count. */
        .ed-stat-clip { overflow: hidden; }
        .ed-stat-grid { margin-right: -1px; margin-bottom: -1px; }
        .ed-stat { border-right: 1px solid ${pal.rule}; border-bottom: 1px solid ${pal.rule}; transition: background 150ms ease; }
        .ed-stat:hover { background: ${pal.card}; }

        .ed-quiet { transition: color 150ms ease, border-color 150ms ease; }
        .ed-quiet:hover { color: ${pal.accent}; border-bottom-color: ${pal.accent}; }
        .ed-danger:hover { color: ${T.accentDeep}; }
        .ed-link { text-decoration: none; border-bottom: 1px solid ${pal.rule}; }
        .ed-link:hover { border-bottom-color: ${pal.accent}; color: ${pal.accent}; }

        .ed-accent { transition: background 150ms ease; }
        .ed-accent:hover { background: ${T.accentDeep}; }
        .ed-out { transition: background 150ms ease, color 150ms ease; }
        .ed-out:hover { background: ${pal.ink}; color: ${pal.paper}; }

        .ed-index-item button { transition: color 150ms ease, border-color 150ms ease; }
        .ed-index-item button:hover { color: ${pal.accent}; }

        .ed-root button:focus-visible,
        .ed-root a:focus-visible,
        .ed-root input:focus-visible {
          outline: 2px solid ${pal.accent}; outline-offset: 3px;
        }

        @media (prefers-reduced-motion: reduce) {
          .ed-stat, .ed-quiet, .ed-accent, .ed-out, .ed-index-item button { transition: none; }
        }

        @media (max-width: 1024px) {
          .ed-body { grid-template-columns: 1fr !important; gap: 28px !important; padding-top: 34px !important; }
          .ed-index { position: static !important; border-right: none !important; padding-right: 0 !important; border-bottom: 1px solid ${pal.rule}; }
          .ed-index-head { display: none; }
          .ed-index-list { display: flex; overflow-x: auto; }
          .ed-index-item { border-top: none !important; flex: 0 0 auto; }
          .ed-index-item button { border-left: none !important; border-bottom: 2px solid transparent; padding: 4px 16px 10px 0 !important; white-space: nowrap; }
          .ed-index-item button[aria-current] { border-bottom-color: ${pal.accent}; }
        }

        @media (max-width: 820px) {
          .ed-stat-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }

        @media (max-width: 560px) {
          .ed-strip { grid-template-columns: 1fr !important; gap: 6px !important; text-align: left !important; }
          .ed-strip > div { text-align: left !important; }
          .ed-fact { grid-template-columns: 1fr !important; }
          .ed-fact dt { border-right: none !important; padding-bottom: 0 !important; }
        }
      `}</style>
    </SheetGround>
  );
};

export default EmployerDashboardPage;
